import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { AgentThreadList, type AgentThreadListProps } from './AgentThreadList'

interface AgentThreadSwitcherProps {
  /** Title of the thread shown in the main panel — the trigger's visible text. */
  title: string
  /** Heading of the sheet and the trigger's accessible prefix (e.g. "Threads"). */
  label: string
  /** Everything the thread list needs; selecting or creating also closes the sheet. */
  list: AgentThreadListProps
}

/**
 * Phone-only replacement for the agent page's thread-list column: a button
 * carrying the active thread's title that opens the same list in a bottom
 * sheet. Hidden from `md`, where the page shows the list as a side column.
 */
export function AgentThreadSwitcher({ title, label, list }: AgentThreadSwitcherProps) {
  const [open, setOpen] = useState(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          className="h-9 min-w-0 flex-1 justify-between md:hidden"
        >
          {title !== label && <span className="sr-only">{label}: </span>}
          <span className="truncate">{title}</span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        aria-describedby={undefined}
        className="max-h-[80dvh] gap-0 bg-card"
      >
        <SheetHeader>
          <SheetTitle>{label}</SheetTitle>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          <AgentThreadList
            {...list}
            inSheet
            onSelect={threadId => {
              list.onSelect(threadId)
              setOpen(false)
            }}
            onCreate={() => {
              list.onCreate()
              setOpen(false)
            }}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
